import { useContext, useEffect, useMemo, useState } from "react"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import AddTaskModal from "../components/AddTaskModal"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { getPaginatedData } from "../../api/apiCalls"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination } from "../../../services/functions"
import BaseButton from "../../../components/elements/BaseButton"
import AllTasksTable from "../components/AllTasksTable"
import BaseInputSearch from "../../../components/elements/BaseInputSearch"
import { useDeleteTaskMutation } from "../api/services"
import { useNavigate } from "react-router-dom"
import { ReactComponent as PLUSCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { ReactComponent as TASKS } from "../../../assets/Task.svg"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import SelectEmployeeModal from "../components/SelectEmployeeModal"
import BaseSelection from "../../../components/elements/BaseSelection"
import { CostCenterType } from "../../Mitarbeiter/types/Types"
import ErrorPage from "../../404/ErrorPage"
import Loading from "../../../components/common/Loading"
import { Pagination } from "antd"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { getCostCenterNames } from "../../Dashboard/api/apiCalls"
import { getEmployeeNamesOnly } from "../../Mitarbeiter/api/apiCalls"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"

export default function TaskOverviewPage() {
    // GET all costcenter
    const {
        error: costcenterError,
        data: costcenterData,
        isLoading: costcenterisLoading,
    } = useQuery({
        queryKey: [GeneralQueryKeys.COSTCENTER_NAMES],
        queryFn: getCostCenterNames,
    })
    const [selectedCostCenter, setselectedCostCenter] = useState<CostCenterType>(
        getDataFromSessionStorage(LocalStorageKeys.TASK_FILTER + "costcenter")
    )
    useEffect(() => costcenterData && setselectedCostCenter(costcenterData[0]), [costcenterData])

    // TASK get all
    const [page, setpage] = useState<number>(getDataFromSessionStorage(LocalStorageKeys.TASK_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        setpage(page)
        sessionStorage.setItem(LocalStorageKeys.TASK_FILTER + "page", JSON.stringify(page))
    }
    const [searchTermTasks, setsearchTermTasks] = useState(getDataFromSessionStorage(LocalStorageKeys.TASK_FILTER + "searchTerm"))
    const searchParamsTasks = useDelayWhenSearchWithPagination(searchTermTasks, LocalStorageKeys.TASK_FILTER + "searchTerm")
    const queryKeyTasks = GeneralQueryKeys.TASK + searchParamsTasks + selectedCostCenter?._id
    const {
        error: allTasksError,
        isLoading: allTasksIsLoading,
        data: allTasksData,
    } = useQuery({
        queryKey: [queryKeyTasks],
        queryFn: () =>
            getPaginatedData({
                pageType: "tasks",
                page: page,
                limit: 20,
                unassigned: true,
                search: searchParamsTasks,
                costCenterId: selectedCostCenter?._id ?? "",
            }),
        enabled: !!selectedCostCenter,
    })
    const displayedTasks = useMemo(() => allTasksData?.docs, [allTasksData?.docs])

    // EMPLOYEE and all stuff according to this
    const [searchTermEmployee, setsearchTermEmployee] = useState("")
    const searchParamsEmployee = useDelayWhenSearchWithPagination(searchTermEmployee)
    const queryKeyEmployee = GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + searchParamsEmployee + selectedCostCenter?._id
    const {
        error: allEmployeeError,
        isLoading: allEmployeeIsLoading,
        data: allEmployeeData,
    } = useQuery({
        queryKey: [queryKeyEmployee],
        queryFn: () => getEmployeeNamesOnly(searchParamsEmployee),
    })

    

    // DELETE TASK
    const queryClient = useQueryClient()
    const { mutate: deleteTask } = useDeleteTaskMutation(queryClient, queryKeyTasks)
    const handleDeleteTask = (id: string) => {
        deleteTask(id)
    }

    // NAVIGATE TO CALENDAR TO ADD SESSION
    const [selectedEmployee, setselectedEmployee] = useState<string>()
    const navigate = useNavigate()

    // FRONTEND LOGIC
    const modalCon = useContext(ModalContext)

    return (
        <>
            {(allTasksError || costcenterError) && <ErrorPage />}
            {(allTasksIsLoading || costcenterisLoading) && <Loading />}
            {costcenterData && allTasksData && (
                <BasePage>
                    <BaseHeadBanner
                        title={"Aufgabenübersicht"}
                        button={<PLUSCIRCLE className="w-8 cursor-pointer h-8" onClick={() => modalCon?.trigger(1)} />}
                    />
                    {/* Button Row */}
                    <BaseButtonRow>
                        <div className="flex gap-2 items-center">
                            <BaseInputSearch
                                placeholder={"Aufgabe oder Objekt suchen..."}
                                searchTerm={searchTermTasks}
                                setsearchTerm={setsearchTermTasks}
                                hidden="hidden lg:flex"
                            />
                            <Authorized allOf={[P.niederlassungen_read]}>
                                <BaseSelection
                                    bgwhite
                                    label={""}
                                    disabled={costcenterData.length === 1}
                                    classProps="!w-64"
                                    value={selectedCostCenter?.name}
                                    data={costcenterData?.map((item: { name: any }) => item.name)}
                                    setId={(e: string) => setselectedCostCenter(costcenterData?.find((item: { name: string }) => item.name === e))}
                                    placeholder={""}
                                />
                            </Authorized>
                        </div>
                        {!allEmployeeError && allEmployeeData && (
                            <div className="flex gap-2">
                                <BaseButton
                                    hideLabelOnMobile="sm"
                                    label={"Aufgabenvorlagen"}
                                    func={() => navigate("/aufgaben-wiederkehrend")}
                                    icon={<TASKS className="w-6 h-6" />}
                                />
                            </div>
                        )}
                    </BaseButtonRow>
                    <BaseGrowBody classProps=" overflow-x-hidden my-4">
                        <AllTasksTable tasks={displayedTasks} deleteTask={handleDeleteTask} openSelectEmployeeModal={() => modalCon?.trigger(0)} />
                    </BaseGrowBody>
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            pageSize={20}
                            defaultCurrent={1}
                            current={page}
                            onChange={handlePageChange}
                            total={allTasksData?.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                    {/* PAGINATION */}
                    <SelectEmployeeModal
                        modalId={0}
                        employees={allEmployeeData}
                        selectData={setselectedEmployee}
                        setSearch={setsearchTermEmployee}
                        customLoading={allEmployeeIsLoading}
                        onClick={() => {
                            modalCon?.trigger(0)
                            navigate("/kalender/" + selectedEmployee)
                        }}
                    />
                    <AddTaskModal
                        modalId={1}
                        type="task"
                        queryKey={queryKeyTasks}
                    />
                </BasePage>
            )}
        </>
    )
}
